import React, { type ComponentProps } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  DisplayText,
  type GiftCardOrderCard,
  LoadingPlaceholder,
  theme,
  useResponsive,
  VStack,
} from '@sg/garnish';

import { useTrackEventEffect } from '@order/Telemetry';

import { GiftCardOrder, GiftCardOrdersEmpty } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardOrders = (props: GiftCardOrdersProps) => {
  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  useTrackEventEffect({ name: 'gift.history_viewed' });

  // ─────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.container}>
        <BodyText style={styles.titleXS} sizeMatch={['16']}>
          {formatMessage(messages.title)}
        </BodyText>

        <GiftCardOrdersContent {...props} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <DisplayText sizeMatch={['32']}>
        {formatMessage(messages.title)}
      </DisplayText>

      <GiftCardOrdersContent {...props} />
    </View>
  );
};

const GiftCardOrdersContent = (props: GiftCardOrdersProps) => {
  const {
    isFetchingOrders,
    giftCardOrders,
    onOpenGiftCardsScreen,
    getConfirmationLink,
    onOrderPress,
  } = props;

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'gift.history_viewed' });

  // ─────────────────────────────────────────────────────────────────────

  if (isFetchingOrders) {
    return (
      <LoadingPlaceholder
        rowHeight={100}
        rows={5}
        gridGap={theme.spacing['6']}
        palette="cream"
      />
    );
  }

  if (giftCardOrders.length === 0) {
    return <GiftCardOrdersEmpty onPress={onOpenGiftCardsScreen} />;
  }

  return (
    <GiftCardOrdersReady
      giftCardOrders={giftCardOrders}
      getConfirmationLink={getConfirmationLink}
      onOrderPress={onOrderPress}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const GiftCardOrdersReady = (
  props: Pick<
    GiftCardOrdersProps,
    'giftCardOrders' | 'getConfirmationLink' | 'onOrderPress'
  >,
) => {
  const { giftCardOrders, getConfirmationLink, onOrderPress } = props;
  const { match } = useResponsive();

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'gift.history_success' });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <VStack
      gap={theme.spacing['10']}
      hasDivider
      dividerColor={theme.colors.LIGHT}
      style={match([undefined, styles.ordersContainerSM])}
    >
      {giftCardOrders.map((order) => {
        const { id, purchasedAt, deliveryDetail, userGiftCards } = order;

        const mainGiftCard = userGiftCards?.[0];
        const { recipientName, balance } = mainGiftCard ?? {};

        return (
          <GiftCardOrder
            key={id}
            id={id}
            recipientName={recipientName}
            deliveryMethods={deliveryDetail.deliveryMethods}
            balance={balance}
            purchaseDate={purchasedAt}
            getConfirmationLink={getConfirmationLink}
            onPress={onOrderPress}
          />
        );
      })}
    </VStack>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Orders',
    description: 'Gift card orders | Title',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['4'],
  },
  titleXS: {
    paddingBottom: theme.spacing['2'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  ordersContainerSM: {
    padding: theme.spacing['6'],
    borderRadius: theme.radius.large,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardOrdersProps = {
  isFetchingOrders: boolean;
  giftCardOrders: ReadonlyArray<{
    id: string;
    purchasedAt: string;
    deliveryDetail: {
      deliveryMethods: ReadonlyArray<string>;
    };
    userGiftCards: ReadonlyArray<{
      id: string;
      balance: number;
      recipientEmail: string | null;
      recipientName: string;
    }>;
  }>;
  onOpenGiftCardsScreen: () => void;
  getConfirmationLink: (
    giftCardOrderId: string,
  ) => ComponentProps<typeof GiftCardOrderCard.Link>['to'];
  onOrderPress?: (giftCardOrderId: string) => void;
};
